import type { ApiClient } from './client';

export type PartnerPeriod = 'week' | 'month' | 'all';
export type PartnerFilter = 'all' | 'buyers' | 'none';
export interface PartnerLevel {
  id: string;
  name: string;
  pct: number;
  buyers: number;
  withdrawable: boolean;
}
export interface PartnerPayee {
  id: string;
  title: string;
  currency: string;
  rate: number;
  bill_to: string;
}
export interface PartnerOverview {
  referral_gift?: { bonus: number; burn_days: number; discount_pct: number; discount_days: number; binding_months: number } | null;
  link: string;
  code: string;
  friends_all: number;
  buyers_all: number;
  earned: number;
  paused: boolean;
  level: PartnerLevel | null;
  next: PartnerLevel | null;
  commission_policy_available: boolean;
  dev_only: boolean;
  levels?: PartnerLevel[];
  months: number;
  period: PartnerPeriod;
  stats: { clicks: number | null; regs: number; buyers: number; earned: number };
  chart: number[];
  chart_from: string;
  chart_unit: string;
  clicks_source_available: boolean;
  terms_url?: string | null;
  withdraw: {
    available: boolean;
    withdrawable: number;
    earned_total: number;
    action: string;
    terms: { min: number; days: number } | null;
    payees: PartnerPayee[];
  };
}
export interface PartnerFriend {
  name: string;
  registered_at: string;
  bound_until: string;
  bought: boolean;
}
export interface PartnerFriends {
  items: PartnerFriend[];
  counts: { all: number; buyers: number; none: number };
  next_cursor: string | null;
}
export interface PartnerWithdrawal {
  id: string;
  at: string;
  circle: number;
  fiat: { amount: number; currency: string };
  status: 'processing' | 'paid' | 'rejected';
  dev_only: boolean;
}
export interface PartnerWithdrawalInput {
  circle: number;
  payee: string;
  requisites: { name: string; tax_id: string };
  invoice_upload_id: string;
}
export type PartnerMaterialKind = 'story' | 'banner' | 'poster' | 'reel';
export interface PartnerMaterialInput {
  template_id?: string;
  theme?: string;
  caption?: string;
}
export interface PartnerMaterialResult {
  media_url?: string;
  expires_in?: number;
  text?: string;
  widget_link?: { url: string; name?: string };
  operation_id?: string;
  state?: string;
  prepared_message_id?: string | null;
  expires_at?: string | null;
  error_code?: string | null;
  delivery_job_id?: string;
  download_url?: string;
  mime?: string;
  order?: { id: number; status: string };
  status?: string;
}
// Каталог определяет сервер: фронт не придумывает доступные шаблоны или условия программы.
export interface PartnerMaterialCatalog {
  available: boolean;
  reason_code: string | null;
  themes: Array<{ id: string; title: string }>;
  items: Array<{
    id: string;
    title: string;
    kind: PartnerMaterialKind | 'qr';
    available: boolean;
    reason_code: string | null;
    mime: string | null;
    width: number | null;
    height: number | null;
    caption_max_length?: number;
    /** Сторис: обложка студии, на которой сервер рисует картинку, и сдвиг кадра по вертикали в процентах */
    background_url?: string | null;
    background_focus_y?: number | null;
  }>;
}
/** Фоны сторис из каталога для BotCore.story.bg: только https-обложки доступных шаблонов */
export function storyBackgrounds(cat: PartnerMaterialCatalog | null | undefined): Record<string, { url: string; y: number }> {
  const out: Record<string, { url: string; y: number }> = {};
  for (const x of cat?.items ?? [])
    if (x.kind === 'story' && x.available && x.background_url && x.background_url.startsWith('https://'))
      out[x.id] = { url: x.background_url, y: x.background_focus_y ?? 30 };
  return out;
}
export const partner = (client: ApiClient) => ({
  overview: (period: PartnerPeriod = 'week', signal?: AbortSignal) =>
    client.request<PartnerOverview>('/app/partner', { query: { period }, signal }),
  friends: (filter: PartnerFilter, cursor?: string, signal?: AbortSignal) =>
    client.request<PartnerFriends>('/app/partner/friends', { query: { filter, cursor }, signal }),
  withdrawals: (signal?: AbortSignal) => client.request<{ items: PartnerWithdrawal[] }>('/app/partner/withdrawals', { signal }),
  withdraw: (body: PartnerWithdrawalInput, key: string) =>
    client.request<PartnerWithdrawal>('/app/partner/withdrawals', { method: 'POST', body, idempotency: key }),
  materials: (signal?: AbortSignal) => client.request<PartnerMaterialCatalog>('/app/partner/materials', { signal }),
  material: (kind: PartnerMaterialKind, body: PartnerMaterialInput, key: string, signal?: AbortSignal) =>
    client.request<PartnerMaterialResult>(`/app/partner/materials/${kind}`, { method: 'POST', body, idempotency: key, signal }),
  qr: (theme: string, signal?: AbortSignal) => client.download(`/app/partner/qr?theme=${encodeURIComponent(theme)}`, signal),
});
